<template>
  <BaseModal :modalTitle="$t('W: Klon prissegment')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmit">
      <UiInputTextFieldVee
        name="name"
        :label="$t('term.name')"
        :rules="['required']"
        isRequired
        :unit="currentLocale.toUpperCase()"
        v-model="form.name" />

      <div class="mt-4">
        <UiComboboxVee
          name="currencyCode"
          :label="$t('term.currency')"
          :options="currencyOptions"
          v-model="form.currencyCode" />
        <p
          v-if="isCurrencyChanged"
          class="-mt-2 text-sm text-text-muted">
          {{ $t('W: Når valutaen ændres, nulstilles priserne i kopien: analyser og tilbud overføres ikke, og priser på svartider og tillægsydelser fjernes.') }}
        </p>
      </div>

      <template #buttons>
        <UiBaseButton
          :text="$t('W: Klon')"
          :isLoading="isLoading"
          :showSaveIcon="showSaveIcon"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { usePricingSegmentStore } from '@/stores/pricingSegmentStore'
import { useModalStore } from '@/stores/modalStore'
import { useAccountStore } from '@/stores/accountStore'
import { useAppStore } from '@/stores/appStore'
import { mapActions, mapState } from 'pinia'
import router from '@/router'
import { useToast } from '@/globals/typescript/utils/toast'
import type { TLabManagementCurrencyCode } from '@team-milva/lab-models/currency'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

export default {
  name: 'ClonePricingSegmentModal',

  props: {
    /** The segment being copied. */
    pricingSegmentID: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      form: {
        name: '',
        currencyCode: '' as TLabManagementCurrencyCode | '',
      },
      isLoading: false,
      showSaveIcon: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(usePricingSegmentStore, ['allPricingSegments']),
    ...mapState(useAccountStore, ['availableCurrencies', 'defaultCurrencyCode']),
    ...mapState(useAppStore, ['currentLocale', 'defaultMessageSaveIconTime']),

    source() {
      return this.allPricingSegments.find((x) => x.id === this.pricingSegmentID)
    },

    /** Changing the currency resets the prices in the copy — the note under the field says so. */
    isCurrencyChanged(): boolean {
      return (
        !!this.source &&
        this.form.currencyCode !== '' &&
        this.form.currencyCode !== this.source.currencyCode
      )
    },

    currencyOptions() {
      const options = this.availableCurrencies.map((currency) => ({
        value: currency.code as TLabManagementCurrencyCode,
        text: `${(currency.name as Record<string, string>)[this.currentLocale] || currency.name['en']} (${currency.code})`,
      }))

      // The source's own currency has to stay selectable even if the lab no longer lists it,
      // otherwise the field would open empty on a segment that is still priced in it.
      const sourceCurrencyCode = this.source?.currencyCode
      if (sourceCurrencyCode && !options.some((x) => x.value === sourceCurrencyCode)) {
        options.push({ value: sourceCurrencyCode, text: sourceCurrencyCode })
      }

      return options.sort((a, b) => a.text.localeCompare(b.text))
    },
  },

  watch: {
    'form.name'() {
      this.errorText = ''
    },
  },

  methods: {
    ...mapActions(usePricingSegmentStore, ['clonePricingSegment']),
    ...mapActions(useModalStore, ['closeTop']),

    async onSubmit(): Promise<void> {
      this.isLoading = true
      this.errorText = ''
      try {
        const id = await this.clonePricingSegment(this.pricingSegmentID, {
          name: this.form.name,
          currencyCode:
            this.form.currencyCode ||
            this.source?.currencyCode ||
            this.defaultCurrencyCode,
        })
        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        useToast().success(this.$t('W: Prissegment klonet'))
        this.closeTop()
        router.push({
          name: 'PricingSegmentEditGeneral',
          params: { segmentID: id },
        })
      } catch {
        this.errorText = this.$t('error.common.standard') as string
        useToast().error(this.errorText)
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    this.form.currencyCode = this.source?.currencyCode ?? this.defaultCurrencyCode
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
